import React, { useState, useRef, useEffect } from 'react';
import './CodeVerification.css';

function CodeVerification({ email, onVerify, onBack, title = 'Enter Verification Code', message }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    // Only allow uppercase letters
    const letter = value.toUpperCase().replace(/[^A-Z]/g, '');

    if (letter.length > 1) {
      // Handle paste
      const letters = letter.split('').slice(0, 6);
      const newCode = ['', '', '', '', '', '']; // Clear all fields first for paste
      letters.forEach((char, i) => {
        if (index + i < 6) {
          newCode[index + i] = char;
        }
      });
      setCode(newCode);

      // Focus last filled input or next empty
      const nextIndex = Math.min(index + letters.length, 5);
      inputRefs.current[nextIndex]?.focus();
    } else if (letter.length === 1) {
      // Single character input - always replace current value
      const newCode = [...code];
      newCode[index] = letter;
      setCode(newCode);

      // Auto-advance to next input
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (letter.length === 0) {
      // Clear the field when empty
      const newCode = [...code];
      newCode[index] = '';
      setCode(newCode);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length === 6 && !isSubmitting) {
      setIsSubmitting(true);
      setError('');

      try {
        await onVerify(fullCode);
        // If successful, component will unmount (user logged in)
      } catch (err) {
        // Show error message
        setError(err.message || 'Invalid code. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const handleClear = () => {
    setCode(['', '', '', '', '', '']);
    inputRefs.current[0]?.focus();
  };

  const isCodeComplete = code.every(letter => letter !== '');

  return (
    <div className="code-verification-container">
      <div className="code-verification-box">
        <h1>{title}</h1>
        <p className="code-message">
          {message || (
            <>A login code has been sent to <strong>{email}</strong></>
          )}
        </p>

        <form onSubmit={handleSubmit} className="code-form">
          <div className="code-inputs">
            {code.map((letter, index) => (
              <React.Fragment key={index}>
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="6"
                  value={letter}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.target.select()}
                  className="code-input"
                  autoComplete="off"
                />
                {index === 2 && <span className="code-separator">-</span>}
              </React.Fragment>
            ))}
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button
              type="submit"
              className="verify-button"
              disabled={!isCodeComplete || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>

            <button
              type="button"
              onClick={handleClear}
              className="clear-button"
              disabled={isSubmitting}
            >
              Clear
            </button>
          </div>

          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="back-button"
            >
              Back to Email
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default CodeVerification;
